import React, { useState, useEffect, useRef } from "react";
import {
  motion,
  useTransform,
  useScroll,
  AnimatePresence,
} from "framer-motion";
import {
  Music,
  ArrowRight,
  MessageCircle,
  Target,
  Zap,
  BarChart,
  PenTool,
  Lightbulb,
  CheckCircle2,
  SkipBack,
} from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";

// Custom Play button with solid fill and rounded corners
const PlayButton = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="white"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 5.14v14.72a1 1 0 001.54.84l10.42-7.36a1 1 0 000-1.68L9.54 4.3A1 1 0 008 5.14z"
      fillRule="evenodd"
      strokeLinecap="round"
      strokeLinejoin="round"
      rx="2"
      ry="2"
    />
  </svg>
);

// Custom Pause button with solid fill and rounded corners
const PauseButton = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="white"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="6" y="4" width="4" height="16" rx="1.5" ry="1.5" />
    <rect x="14" y="4" width="4" height="16" rx="1.5" ry="1.5" />
  </svg>
);

// Custom Audio Player Component
const AudioPlayer = ({ audioUrl, title, onPlayingChange }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);

  useEffect(() => {
    // Reset player when audio URL changes
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [audioUrl]);

  // Notify parent component about playing state changes
  useEffect(() => {
    if (onPlayingChange) {
      onPlayingChange(isPlaying);
    }
  }, [isPlaying, onPlayingChange]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch((err) => {
          console.error("Error playing audio:", err);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const durationTime = audioRef.current.duration;
      setCurrentTime(current);
      setProgress((current / durationTime) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleProgressClick = (e) => {
    if (progressBarRef.current && audioRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      const newTime = pos * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
      setProgress(pos * 100);
    }
  };

  const resetAudio = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      setProgress(0);
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setProgress(0);
      setCurrentTime(0);
    }
  };

  return (
    <div className="custom-audio-player w-full max-w-[300px]">
      <audio
        ref={audioRef}
        src={audioUrl}
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        className="hidden"
      />

      <div className="flex flex-col items-center">
        {/* Play/Pause Button */}
        <motion.button
          onClick={togglePlay}
          className="relative flex items-center justify-center w-28 h-28 mb-5 rounded-full bg-gradient-to-br from-[var(--primary-color)] to-[var(--primary-color-dark,_var(--primary-color))] text-white shadow-lg z-10"
          whileHover={{
            scale: 1.05,
            boxShadow: "0 0 25px rgba(var(--primary-rgb), 0.5)",
          }}
          whileTap={{ scale: 0.95 }}
          disabled={!audioUrl}
          aria-label={isPlaying ? `Pause ${title}` : `Play ${title}`}
        >
          <motion.div
            className="absolute inset-0 rounded-full bg-white opacity-10"
            animate={{
              scale: isPlaying ? [1, 1.15, 1] : 1,
              opacity: isPlaying ? [0.1, 0.2, 0.1] : 0.1,
            }}
            transition={{
              duration: 2.5,
              repeat: isPlaying ? Infinity : 0,
              repeatType: "loop",
            }}
          />
          {isPlaying ? <PauseButton size={40} /> : <PlayButton size={40} />}
        </motion.button>

        {/* Custom Progress Bar */}
        <div className="w-full px-2">
          <div
            ref={progressBarRef}
            className="relative h-2.5 bg-white/10 rounded-full cursor-pointer overflow-hidden"
            onClick={handleProgressClick}
          >
            <motion.div
              className="absolute top-0 left-0 h-full bg-[var(--primary-color)]"
              style={{ width: `${progress}%` }}
              animate={{ width: `${progress}%` }}
              transition={{ type: "tween", duration: 0.1 }}
            />
          </div>

          {/* Time display with reset functionality */}
          <div className="flex justify-between items-center mt-2">
            <div className="flex items-center gap-2">
              <button
                onClick={resetAudio}
                className="py-1.5 px-2.5 rounded-md bg-white/5 hover:bg-white/15 text-sm font-medium text-white/70 hover:text-white transition-colors flex items-center gap-1.5 z-20 relative"
                title="Reset to beginning"
              >
                <SkipBack size={16} />
                <span>{formatTime(currentTime)}</span>
              </button>
            </div>
            <span className="text-xs font-medium text-white/70">
              {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeliverySection = ({
  deliverySections,
  scrollProgress,
  headerStyle,
  textStyle,
  profileId,
  backgroundImage,
  palette,
}) => {
  const [selectedSection, setSelectedSection] = useState(0);
  const [videoError, setVideoError] = useState({});
  const [audioError, setAudioError] = useState({});
  const [isHovered, setIsHovered] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const videoRefs = useRef([]);
  const audioRefs = useRef([]);
  const ref = useRef(null);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  // Array of paragraph icons to cycle through
  const paragraphIcons = [
    <CheckCircle2
      key="check"
      className="text-[var(--primary-color)]"
      size={22}
    />,
    <ArrowRight
      key="arrow"
      className="text-[var(--primary-color)]"
      size={22}
    />,
    <Target key="target" className="text-[var(--primary-color)]" size={22} />,
    <MessageCircle
      key="message"
      className="text-[var(--primary-color)]"
      size={22}
    />,
    <Zap key="zap" className="text-[var(--primary-color)]" size={22} />,
    <BarChart key="chart" className="text-[var(--primary-color)]" size={22} />,
    <PenTool key="pen" className="text-[var(--primary-color)]" size={22} />,
    <Lightbulb
      key="lightbulb"
      className="text-[var(--primary-color)]"
      size={22}
    />,
  ];

  // Setup section-specific scroll animations
  const { scrollYProgress: sectionProgress } = useScroll({
    target: ref,
    offset: ["start 0.8", "end start"],
  });

  // Removed section opacity animations to prevent gradient effect

  // Background parallax effect
  const bgY = useTransform(sectionProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(sectionProgress, [0, 1], [1, 1.1]);

  // Title animation
  const titleOpacity = useTransform(sectionProgress, [0.05, 0.25], [0, 1]);
  const titleY = useTransform(sectionProgress, [0.05, 0.25], [30, 0]);

  // Initialize refs when sections change
  useEffect(() => {
    if (deliverySections?.sections) {
      videoRefs.current = deliverySections.sections.map(
        (_, i) => videoRefs.current[i] || React.createRef()
      );
      audioRefs.current = deliverySections.sections.map(
        (_, i) => audioRefs.current[i] || React.createRef()
      );
    }
  }, [deliverySections?.sections]);

  // Handle video and audio errors
  const handleMediaError = (index, type) => {
    if (type === "video") {
      setVideoError((prev) => ({ ...prev, [index]: true }));
    } else {
      setAudioError((prev) => ({ ...prev, [index]: true }));
    }
  };

  // Function to render media based on type
  const renderMedia = (section, index) => {
    const { mediaType, mediaUrl } = section;

    if (!mediaUrl) {
      return (
        <div className="w-full aspect-video flex items-center justify-center rounded-xl bg-black/30 text-white/50">
          No media provided
        </div>
      );
    }

    if (mediaType === "video" && !videoError[index]) {
      return (
        <div className="w-full aspect-video rounded-2xl overflow-hidden bg-black shadow-xl border border-white/10 relative group">
          {/* Overlay with play indicator on hover */}
          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300 pointer-events-none">
            <div className="w-20 h-20 rounded-full bg-[var(--primary-color)]/80 flex items-center justify-center">
              <PlayButton size={32} />
            </div>
          </div>

          <video
            ref={(el) => (videoRefs.current[index] = el)}
            controls
            className="w-full h-full object-cover"
            onError={() => handleMediaError(index, "video")}
          >
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    } else if (mediaType === "audio" && !audioError[index]) {
      return (
        <div
          className={`w-full p-8 rounded-2xl bg-gradient-to-br from-black/40 to-black/20 flex flex-col items-center justify-center shadow-lg relative ${
            isAudioPlaying
              ? "border border-[var(--primary-color)] shadow-[0_0_15px_rgba(var(--primary-rgb),0.3)]"
              : "border border-white/10"
          }`}
        >
          {/* Sound wave animation overlay when playing */}
          {isAudioPlaying && (
            <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
              <div className="absolute inset-x-0 bottom-0 h-16 opacity-10">
                <div className="flex items-end justify-around h-full px-2">
                  {[...Array(16)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="bg-[var(--primary-color)] w-1.5 rounded-t-full"
                      animate={{
                        height: [
                          `${Math.random() * 30 + 10}%`,
                          `${Math.random() * 70 + 30}%`,
                          `${Math.random() * 30 + 10}%`,
                        ],
                      }}
                      transition={{
                        duration: 1 + Math.random(),
                        repeat: Infinity,
                        repeatType: "reverse",
                        ease: "easeInOut",
                        delay: Math.random() * 0.5,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          <AudioPlayer
            audioUrl={mediaUrl}
            title={section.title || `Audio ${index + 1}`}
            onPlayingChange={setIsAudioPlaying}
          />
        </div>
      );
    } else {
      // Fallback for error state
      return (
        <div className="w-full aspect-video flex items-center justify-center rounded-xl bg-black/30 text-white/50">
          Media could not be loaded
        </div>
      );
    }
  };

  // Download all audio archive files as ZIP
  const handleDownloadAudioArchive = async () => {
    setDownloadProgress(0);
    setDownloadError(null);
    try {
      const audioArchive = deliverySections.audioArchive || [];
      if (!audioArchive.length) return;
      const zip = new JSZip();
      let completed = 0;
      for (const file of audioArchive) {
        const response = await fetch(file.url);
        if (!response.ok) throw new Error(`Failed to fetch ${file.name}`);
        const blob = await response.blob();
        zip.file(file.name, blob);
        completed++;
        setDownloadProgress(
          Math.round((completed / audioArchive.length) * 100)
        );
      }
      const content = await zip.generateAsync({ type: "blob" }, (metadata) => {
        setDownloadProgress(Math.round(metadata.percent));
      });
      saveAs(content, "brand-audio-archive.zip");
      setDownloadProgress(null);
    } catch (err) {
      setDownloadError(err.message);
      setDownloadProgress(null);
    }
  };

  if (!deliverySections?.sections || deliverySections.sections.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white pt-[25vh]">
        <h2
          className="text-5xl md:text-7xl font-bold text-center mb-8 text-white"
          style={{ textShadow: "0 2px 10px rgba(0, 0, 0, 0.6)" }}
        >
          Delivery
        </h2>

        <div className="max-w-6xl mx-auto px-6 bg-white/5 rounded-xl p-10 text-center">
          <h3 className="text-2xl font-medium mb-4">No delivery content yet</h3>
          <p className="text-white/70 mb-4">
            There are no delivery sections in this profile. Please add content
            in the admin editor.
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.section
      ref={ref}
      className="relative flex flex-col justify-start py-0 mt-20 overflow-hidden z-10"
    >
      {/* Custom CSS for audio elements */}
      <style jsx>{`
        audio {
          width: 100%;
          height: 10px;
          border-radius: 8px;
          background-color: rgba(255, 255, 255, 0.03);
          filter: contrast(1.1);
        }

        audio::-webkit-media-controls-panel {
          background-color: rgba(15, 15, 15, 0.6);
        }

        audio::-webkit-media-controls-time-remaining-display,
        audio::-webkit-media-controls-current-time-display {
          color: white;
        }

        audio::-webkit-media-controls-play-button,
        audio::-webkit-media-controls-timeline {
          filter: brightness(1.5);
        }
      `}</style>

      {/* Removed dot pattern background with gradient */}

      {/* Background image with parallax effect */}
      {backgroundImage && (
        <motion.div
          className="absolute inset-0 w-full h-full bg-center bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            y: bgY,
            scale: bgScale,
            opacity: 0.5,
            filter: "contrast(1.1) brightness(1.05)",
            mixBlendMode: "soft-light",
          }}
        />
      )}

      {/* Content container */}
      <motion.div className="relative z-10 w-full mx-auto pt-0">
        {/* Main title - centered */}
        <div className="max-w-7xl mx-auto px-6 mb-8">
          <motion.h2 className="text-5xl md:text-7xl mb-8 font-bold text-white">
            Delivery
          </motion.h2>
        </div>

        {/* Audio Archive Download Button */}
        {deliverySections.audioArchive &&
          deliverySections.audioArchive.length > 0 && (
            <div className="flex flex-col items-center mb-10">
              <button
                onClick={handleDownloadAudioArchive}
                className="px-6 py-3 rounded-lg bg-[var(--primary-color)] text-white font-semibold shadow hover:bg-[var(--primary-color-dark)] transition-colors mb-2"
                disabled={downloadProgress !== null}
              >
                {downloadProgress !== null
                  ? `Downloading... ${downloadProgress}%`
                  : "Download All Audio Files (ZIP)"}
              </button>
              {downloadError && (
                <div className="text-red-400 text-sm mt-1">{downloadError}</div>
              )}
              <div className="text-xs text-white/60">
                This will download all audio files as a single ZIP archive.
              </div>
            </div>
          )}

        {/* Display all sections vertically instead of tabs */}
        <div className="space-y-24 max-w-7xl mx-auto px-6 mb-16">
          {deliverySections.sections.map((section, sectionIndex) => (
            <motion.div
              key={sectionIndex}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 shadow-lg transition-all duration-300"
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.1 * sectionIndex }}
              whileHover={{
                scale: 1.01,
                borderColor: "rgba(1, 178, 128, 0.3)",
              }}
            >
              {/* Section Title */}
              <div className="flex items-center justify-between mb-10">
                <motion.h3
                  className="text-3xl md:text-4xl font-bold text-white"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  {section.title || `Delivery ${sectionIndex + 1}`}
                </motion.h3>
                <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center transition-all duration-300 hover:bg-[var(--primary-color)]">
                  <Music className="w-7 h-7 text-white" />
                </div>
              </div>

              {/* Sound wave decoration */}
              <div className="absolute right-0 top-20 w-1/3 h-40 opacity-10 z-0 pointer-events-none">
                <svg viewBox="0 0 800 200" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0,100 Q200,180 400,100 T800,100"
                    fill="none"
                    stroke="var(--primary-color)"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                  <path
                    d="M0,100 Q200,20 400,100 T800,100"
                    fill="none"
                    stroke="var(--primary-color)"
                    strokeWidth="2"
                  />
                  <path
                    d="M0,100 Q200,150 400,100 T800,100"
                    fill="none"
                    stroke="var(--primary-color)"
                    strokeWidth="1"
                  />
                </svg>
              </div>

              <div className="flex flex-col">
                {/* Media Section - Full Width and Centered */}
                <motion.div
                  className="w-full mx-auto mb-16 max-w-5xl"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  {renderMedia(section, sectionIndex)}
                </motion.div>

                {/* Text Content - Stacked Below */}
                {section.paragraphs?.length > 0 && (
                  <div className="w-full mx-auto max-w-5xl">
                    <div className="space-y-8">
                      {section.paragraphs
                        .filter((paragraph) => paragraph.trim())
                        .map((paragraph, idx) => (
                          <motion.div
                            key={idx}
                            className="flex items-start gap-5 p-4 rounded-xl transition-all duration-300 border border-transparent hover:border-[var(--primary-color)]/20 group"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{
                              duration: 0.5,
                              delay: 0.1 * idx + 0.4,
                            }}
                            whileHover={{
                              backgroundColor: "rgba(255, 255, 255, 0.05)",
                              scale: 1.01,
                              transition: { duration: 0.2 },
                            }}
                          >
                            <div className="flex-shrink-0 mt-1.5">
                              <motion.div
                                className="group-hover:bg-[var(--primary-color)] w-10 h-10 rounded-full bg-white/10 flex items-center justify-center transition-all duration-300"
                                whileHover={{ scale: 1.1 }}
                                animate={{
                                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                                }}
                                transition={{ duration: 0.3 }}
                                style={{
                                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
                                }}
                              >
                                {paragraphIcons[idx % paragraphIcons.length]}
                              </motion.div>
                            </div>
                            <div className="flex-1">
                              <p
                                className="text-xl md:text-2xl font-medium leading-relaxed text-white/90 group-hover:text-white transition-colors duration-300 whitespace-pre-wrap"
                                style={{
                                  ...textStyle,
                                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
                                  letterSpacing: "0.01em",
                                }}
                              >
                                {paragraph}
                              </p>
                              <div
                                className="w-0 group-hover:w-full h-0.5 bg-gradient-to-r from-[var(--primary-color)] to-transparent mt-2 transition-all duration-500 opacity-0 group-hover:opacity-100"
                                style={{ transitionDelay: "0.1s" }}
                              />
                            </div>
                          </motion.div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </motion.section>
  );
};

export default DeliverySection;
